<div class="identity-hub">
  <header class="identity-hub__header">
    <div class="identity-hub__header-icon" aria-hidden="true">
      <img src="/public/svgs/identity-id.svg" alt="" width="22" height="22" />
    </div>
    <div>
      <h1 class="identity-hub__title">
        {{ 'app.identity.hub.title' | translate }}
      </h1>
      <p class="identity-hub__subtitle">
        {{ 'app.identity.hub.subtitle' | translate }}
      </p>
    </div>
  </header>

  @if (!hasAttention()) {
    <section class="identity-hub__all-clear" role="status">
      <p class="identity-hub__all-clear-title">
        {{ 'app.identity.hub.allClearTitle' | translate }}
      </p>
      <p class="identity-hub__all-clear-body">
        {{ 'app.identity.hub.allClearBody' | translate }}
      </p>
    </section>
  }

  <section class="identity-hub__section" aria-labelledby="identity-hub-parent-heading">
    <h2 id="identity-hub-parent-heading" class="identity-hub__section-title">
      {{ 'app.identity.hub.parentSection' | translate }}
    </h2>
    <app-parent-identity-section />
  </section>

  <section
    class="identity-hub__section identity-hub__panel"
    aria-labelledby="identity-hub-disclosure-heading"
  >
    <div class="identity-hub__section-head">
      <h2 id="identity-hub-disclosure-heading" class="identity-hub__section-title">
        {{ 'app.identity.hub.disclosureSection' | translate }}
      </h2>
      @if (pendingDisclosureCount() > 0) {
        <span class="identity-hub__pill">
          {{
            'app.identity.hub.pendingCount'
              | translate: { count: pendingDisclosureCount() }
          }}
        </span>
      }
    </div>

    <p class="identity-hub__copy">
      {{ 'app.identity.hub.connectedAppsIntro' | translate }}
    </p>

    <div class="identity-hub__stat-grid">
      <button
        type="button"
        class="identity-hub__stat-card"
        (click)="openDisclosureGrants()"
      >
        <span class="identity-hub__stat-label">
          {{ 'app.identity.hub.grantsSection' | translate }}
        </span>
        <span class="identity-hub__stat-value">{{ activeGrantCount() }}</span>
        <span class="identity-hub__stat-hint">
          @if (activeGrantCount() === 0) {
            {{ 'app.identity.hub.grantsEmptyShort' | translate }}
          } @else if (activeGrantCount() === 1) {
            {{ 'app.identity.hub.grantsActiveSingleShort' | translate }}
          } @else {
            {{
              'app.identity.hub.grantsActiveMultipleShort'
                | translate: { count: activeGrantCount() }
            }}
          }
        </span>
        <span class="identity-hub__stat-cta">
          {{ 'app.identity.hub.grantsManageCta' | translate }}
        </span>
      </button>

      <button
        type="button"
        class="identity-hub__stat-card"
        [class.identity-hub__stat-card--alert]="pendingDisclosureCount() > 0"
        (click)="openDisclosureApprovals()"
      >
        <span class="identity-hub__stat-label">
          {{ 'app.identity.hub.pendingApprovalsLabel' | translate }}
        </span>
        <span class="identity-hub__stat-value">{{ pendingDisclosureCount() }}</span>
        <span class="identity-hub__stat-hint">
          @if (pendingDisclosureCount() === 0) {
            {{ 'app.identity.hub.disclosureEmpty' | translate }}
          } @else if (pendingDisclosureCount() === 1) {
            {{ 'app.identity.hub.disclosurePendingSingle' | translate }}
          } @else {
            {{
              'app.identity.hub.disclosurePendingMultiple'
                | translate: { count: pendingDisclosureCount() }
            }}
          }
        </span>
        <span class="identity-hub__stat-cta">
          {{ 'app.identity.hub.disclosureReviewCta' | translate }}
        </span>
      </button>
    </div>
  </section>

  <section class="identity-hub__section" aria-labelledby="identity-hub-children-heading">
    <div class="identity-hub__section-head">
      <h2 id="identity-hub-children-heading" class="identity-hub__section-title">
        {{ 'app.identity.hub.childrenSection' | translate }}
      </h2>
      @if (children().length > 0) {
        <span class="identity-hub__count">
          {{
            'app.identity.hub.childrenCount'
              | translate: { count: children().length }
          }}
        </span>
      }
      @if (unverifiedKids().length > 0) {
        <span class="identity-hub__pill">
          {{
            'app.identity.hub.pendingCount'
              | translate: { count: unverifiedKids().length }
          }}
        </span>
      }
    </div>

    @if (children().length === 0) {
      <p class="identity-hub__empty">
        {{ 'app.identity.hub.childrenEmpty' | translate }}
      </p>
    } @else {
      @if (unverifiedKids().length > 0) {
        <ul class="identity-hub__kid-list identity-hub__kid-list--priority">
          @for (child of unverifiedKids(); track child.id) {
            <li class="identity-hub__kid-row identity-hub__kid-row--pending">
              <div class="identity-hub__kid-avatar" aria-hidden="true">
                {{ kidInitial(child) }}
              </div>
              <div class="identity-hub__kid-copy">
                <span class="identity-hub__kid-name">{{ kidName(child) }}</span>
                <span class="identity-hub__kid-badge identity-hub__kid-badge--pending">
                  {{ 'app.identity.hub.childPending' | translate }}
                </span>
              </div>
              <app-parent-button
                variant="primary"
                (clicked)="openKidVerification(child)"
              >
                {{ 'app.identity.hub.verifyChildCta' | translate }}
              </app-parent-button>
            </li>
          }
        </ul>
      }

      @if (verifiedKids().length > 0) {
        <div class="identity-hub__verified-block">
          <div class="identity-hub__verified-head">
            <p class="identity-hub__verified-title">
              {{
                'app.identity.hub.verifiedChildren'
                  | translate: { count: verifiedKids().length }
              }}
            </p>
            @if (verifiedKids().length > verifiedPreviewLimit) {
              <button
                type="button"
                class="identity-hub__toggle"
                (click)="toggleVerifiedKids()"
              >
                @if (showAllVerified()) {
                  {{ 'app.identity.hub.showLessChildren' | translate }}
                } @else {
                  {{
                    'app.identity.hub.showAllChildren'
                      | translate: { count: verifiedKids().length }
                  }}
                }
              </button>
            }
          </div>

          <ul
            class="identity-hub__kid-list identity-hub__kid-list--compact"
            [class.identity-hub__kid-list--scroll]="showAllVerified()"
          >
            @for (child of visibleVerifiedKids(); track child.id) {
              <li class="identity-hub__kid-row identity-hub__kid-row--compact">
                <div class="identity-hub__kid-avatar" aria-hidden="true">
                  {{ kidInitial(child) }}
                </div>
                <span class="identity-hub__kid-name">{{ kidName(child) }}</span>
                <span class="identity-hub__kid-badge identity-hub__kid-badge--verified">
                  {{ 'app.identity.hub.childVerified' | translate }}
                </span>
              </li>
            }
          </ul>
        </div>
      }
    }
  </section>
</div>
